import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'

import { useContext } from 'react'
import { useQuery, useMutation, asyncClientQuery, StatusIndicatorContext } from 'components/graphql'
import { useApolloClient } from '@apollo/client'
import { Row, Col } from 'react-flexbox-grid'
import { Select, Number as NumberInput } from 'components/forms'
import { multiValidate, notEmpty, notNegative, minMaxFields, minMaxVal } from 'helpers/validators'
import appConfig from 'gql/query/appConfig/'
import updateOfferGroupsMutation from 'gql/mutation/updateOfferGroups'
import deleteOfferGroupMutation from 'gql/mutation/deleteOfferGroup'
import getOfferGroupUsageCountQuery from 'gql/query/getOfferGroupUsageCount'
import confirmation from 'components/Modal/components/ConfirmModal'
import TagEditor from 'components/TagEditor'
import Divider from 'components/Divider'
import { StyledSubLabel, StyledNoMarginWrapper, StyledSelectWithAction } from './StyledComponents'

const TradePrices = ({ mode }) => {
	const [t] = useTranslation()

	const getValidation = (relatedField, ind) => {
		const validate = (val, vals) => {
			const relatedVal = get(vals, ['tradePrice', ind, relatedField])
			if(relatedVal) {
				return notEmpty().validate(val)
			}
			return
		}
		const notify = [`tradePrice[${ind}].${relatedField}`]
		return { validate, notify, validateOnChange: true }
	}

	const tradePrices = []
	for (let i = 0; i < 5; i++) {
		const item = (
			<Col key={i} xs className='narrow-col'>
				<StyledSubLabel className='form-group-label' isVisible={!i}>
					{t('common.fields.quantity')}
				</StyledSubLabel>
				<NumberInput
					field={`tradePrice[${i}].count`}
					placeholder={`Menge ${i + 1}`}
					{...getValidation('price', i)}
					{...i === 0 && notEmpty()}
					withControls={false}
					shrink />
				<StyledSubLabel className='form-group-label' isVisible={!i}>
					{mode === 0 ? t('product_section.strings.sum') : t('product_section.strings.price')}
				</StyledSubLabel>
				<NumberInput
					field={`tradePrice[${i}].price`}
					placeholder={`Preis ${i + 1}`}
					format='currency'
					{...getValidation('count', i)}
					{...i === 0 && multiValidate([notEmpty(), notNegative()])}
					withControls={false}
					shrink
					toFixed={2}
					precision={2} />
			</Col>
		)
		tradePrices.push(item)
	}

	return <Row>{tradePrices}</Row>
}

const TradePriceDetails = ({ mode }) => {
	const [t] = useTranslation()
	const { data, queryField } = useQuery(appConfig.actionGroups)
	const client = useApolloClient()
	const { addPromise } = useContext(StatusIndicatorContext)
	const [updateOfferGroups] = useMutation(updateOfferGroupsMutation, {
		refetchQueries: [{ query: appConfig.actionGroups }]
	})
	const [deleteOfferGroup] = useMutation(deleteOfferGroupMutation, {
		refetchQueries: [{ query: appConfig.actionGroups }]
	})
	const groups = get(data, [queryField, 'productActionGroups']) || []
	const getActionGroupOptions = () => groups.map((item, i) => ({ value: i + 1, label: item }))
	const offerGroupsSelect = groups.map((name, i) => ({ id: i, name }))
	const customActions = {
		handlerCreate: ({ setValue, value }) => {
			if(!value) return
			updateOfferGroups({ variables: { fields: { productActionGroups: [...groups, value] } } })
			setValue('')
		},
		handlerUpdate: (tag, value) => {
			const updated = groups.map((g, i) => i === tag.id ? value.trim() : g)
			updateOfferGroups({ variables: { fields: { productActionGroups: updated } } })
		},
		handlerDelete: async (tag) => {
			const groupIndex = tag.id + 1
			const countPromise = asyncClientQuery(client, getOfferGroupUsageCountQuery, {
				variables: { groupIndex },
				fetchPolicy: 'network-only'
			})
			addPromise(countPromise)
			const { data: countData, queryField: countField } = await countPromise
			const count = countData[countField]
			if(count > 0) {
				try {
					await confirmation({
						text: `${count} Produkt${count === 1 ? '' : 'e'} verlier${count === 1 ? 't' : 'en'} ihre Gruppenzuweisung. Fortfahren?`
					})
				} catch {
					return
				}
			}
			deleteOfferGroup({ variables: { groupIndex } })
		}
	}
	const emptyValidation = notEmpty()

	return(
		<Fragment>
			<Row>
				<Col xs>
					<Select
						field='mode'
						label={t('product_section.strings.mode')}
						options={[
							{ value: 0, label: 'Mengenangebot Stück' },
							{ value: 2, label: 'Mengenangebot Aktion "3 für 2"' },
							{ value: 3, label: 'Mengenangebot 100% Rabatt für das günstigste Stück' },
							{ value: 4, label: 'Mengenangebot Preis ab Menge' }
						]} />
				</Col>
				<Col xs>
					{(mode === 3) &&
						<StyledSelectWithAction>
							<Select
								field='actionGroup'
								options={getActionGroupOptions()}
								label={t('product_section.strings.group')}
								placeholder='Gruppe auswählen'
								style={{ width: '100%' }}
								{...mode === 3 && { ...notEmpty(), validateOnChange: true }} />
							<TagEditor
								fieldsSelect={offerGroupsSelect}
								customActions={customActions} />
						</StyledSelectWithAction>
					}
					{mode === 1 &&
						<NumberInput
							field='actionDiscount'
							label={`${t('product_section.strings.discount')}, %`}
							{...multiValidate([notEmpty(), minMaxVal({ max: 100 })])}
							withControls={false} />
					}
					{mode === 2 &&
						<StyledNoMarginWrapper>
							<NumberInput
								field='actionAmount.more'
								label={`${t('common.fields.quantity')} 3`}
								{...multiValidate([notEmpty(), notNegative(), minMaxFields('max', 'actionAmount.less')])}
								withControls={false}
								shrink
								inline />
							<NumberInput
								field='actionAmount.less'
								label={`${t('common.fields.quantity')} 2`}
								{...multiValidate([notEmpty(), notNegative(), minMaxFields('min', 'actionAmount.more')])}
								withControls={false}
								shrink
								inline />
						</StyledNoMarginWrapper>
					}
				</Col>
				<Col xs>
					{mode === 3 &&
						<NumberInput
							field='tradePrice[0].count'
							label={t('product_section.strings.amount_from')}
							validate={val => emptyValidation.validate(val) || (val < 2 ? 'Value must be greater 1' : null)}
							withControls={false} />
					}
				</Col>
			</Row>
			{(mode === 0 || mode === 4) &&
				<Fragment>
					<Divider />
					<p className='form-group-label'>{t('product_section.strings.price_offer')}</p>
					<TradePrices mode={mode} />
				</Fragment>
			}
		</Fragment>
	)
}

export default TradePriceDetails
